import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Calendar, Check, X, Clock, User, Mail, Phone, MapPin, Trash2, RefreshCw, Filter } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_TABS = [
  { key: 'pending', label: 'Pending', color: 'text-yellow-400', dot: 'bg-yellow-400' },
  { key: 'confirmed', label: 'Confirmed', color: 'text-green-400', dot: 'bg-green-400' },
  { key: 'declined', label: 'Declined', color: 'text-red-400', dot: 'bg-red-400' },
  { key: 'all', label: 'All', color: 'text-gray-300', dot: 'bg-gray-400' },
];

const AdminBookings = () => {
  const { toast } = useToast();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('pending');
  const [typeFilter, setTypeFilter] = useState('all');

  const fetchBookings = async () => {
    setLoading(true);
    try {
      const params = {};
      if (activeTab !== 'all') params.status = activeTab;
      if (typeFilter !== 'all') params.talent_type = typeFilter;
      const res = await axios.get(`${API}/bookings`, { params });
      setBookings(res.data);
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to load bookings.', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBookings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, typeFilter]);

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/bookings/${id}/status`, null, { params: { status } });
      toast({ title: `Booking ${status}`, description: 'Client has been notified by email.' });
      fetchBookings();
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to update booking.', variant: 'destructive' });
    }
  };

  const deleteBooking = async id => {
    if (!window.confirm('Delete this booking? This cannot be undone.')) return;
    try {
      await axios.delete(`${API}/bookings/${id}`);
      toast({ title: 'Deleted', description: 'Booking removed.' });
      fetchBookings();
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to delete booking.', variant: 'destructive' });
    }
  };

  const counts = bookings.reduce(
    (acc, b) => {
      acc[b.status] = (acc[b.status] || 0) + 1;
      return acc;
    },
    { pending: 0, confirmed: 0, declined: 0 }
  );

  return (
    <div className="bg-black text-white min-h-screen pt-32 pb-20">
      <div className="container mx-auto px-4 lg:px-8">
        <Link
          to="/ews-admin-panel"
          className="inline-flex items-center gap-2 text-gray-400 hover:text-red-400 transition-colors mb-6"
          data-testid="back-to-admin"
        >
          <ArrowLeft size={18} /> Back to Admin
        </Link>

        <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
          <div>
            <h1 className="font-display text-4xl md:text-5xl font-bold text-white flex items-center gap-3">
              <Calendar className="text-red-500" size={36} /> Bookings
            </h1>
            <p className="text-gray-400 mt-2">Manage booking requests for models and professionals.</p>
          </div>
          <button
            onClick={fetchBookings}
            className="inline-flex items-center gap-2 bg-zinc-900 hover:bg-zinc-800 border border-red-900/30 px-4 py-2 rounded-full text-sm transition-colors"
            data-testid="refresh-bookings"
          >
            <RefreshCw size={14} /> Refresh
          </button>
        </div>

        {/* Status tabs */}
        <div className="flex flex-wrap gap-2 mb-4">
          {STATUS_TABS.map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-mono tracking-wider uppercase border transition-all ${
                activeTab === tab.key
                  ? 'border-red-500 bg-red-500/10 text-white'
                  : 'border-zinc-800 text-gray-400 hover:border-red-900/50'
              }`}
              data-testid={`bookings-tab-${tab.key}`}
            >
              <span className={`w-2 h-2 rounded-full ${tab.dot}`}></span>
              {tab.label}
              {tab.key !== 'all' && counts[tab.key] > 0 && (
                <span className="bg-red-600/80 text-white text-xs px-2 py-0.5 rounded-full ml-1">
                  {counts[tab.key]}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Type filter */}
        <div className="flex items-center gap-2 mb-8 text-sm">
          <Filter size={14} className="text-gray-500" />
          <span className="text-gray-500 mr-2">Type:</span>
          {['all', 'model', 'professional'].map(t => (
            <button
              key={t}
              onClick={() => setTypeFilter(t)}
              className={`px-3 py-1 rounded-full text-xs uppercase tracking-wider transition-colors ${
                typeFilter === t
                  ? 'bg-red-600 text-white'
                  : 'bg-zinc-900 text-gray-400 hover:text-white'
              }`}
              data-testid={`type-filter-${t}`}
            >
              {t}
            </button>
          ))}
        </div>

        {/* Bookings list */}
        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-8 h-8 border-2 border-red-500 border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : bookings.length === 0 ? (
          <div className="text-center py-16 bg-zinc-900/50 rounded-lg border border-red-900/20">
            <Calendar className="mx-auto text-gray-600 mb-4" size={40} />
            <p className="text-gray-400">No bookings found in this view.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {bookings.map(b => (
              <BookingCard
                key={b.id}
                booking={b}
                onConfirm={() => updateStatus(b.id, 'confirmed')}
                onDecline={() => updateStatus(b.id, 'declined')}
                onDelete={() => deleteBooking(b.id)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const StatusBadge = ({ status }) => {
  const map = {
    pending: 'bg-yellow-500/15 text-yellow-300 border-yellow-500/30',
    confirmed: 'bg-green-500/15 text-green-300 border-green-500/30',
    declined: 'bg-red-500/15 text-red-300 border-red-500/30',
    cancelled: 'bg-gray-500/15 text-gray-300 border-gray-500/30',
  };
  return (
    <span
      className={`px-3 py-1 text-xs uppercase tracking-wider rounded-full border ${map[status] || map.pending}`}
    >
      {status}
    </span>
  );
};

const BookingCard = ({ booking, onConfirm, onDecline, onDelete }) => (
  <div
    className="bg-zinc-900 border border-red-900/20 hover:border-red-600/40 rounded-lg p-6 transition-colors"
    data-testid={`booking-${booking.id}`}
  >
    <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
      <div>
        <div className="flex items-center gap-3 mb-1">
          <h3 className="text-xl font-semibold text-white">{booking.talent_name}</h3>
          <span className="text-xs font-mono uppercase text-red-400 bg-red-500/10 px-2 py-1 rounded">
            {booking.talent_type}
          </span>
          <StatusBadge status={booking.status} />
        </div>
        <p className="text-sm text-gray-400 flex flex-wrap items-center gap-3">
          <span className="inline-flex items-center gap-1.5">
            <Calendar size={13} /> {booking.booking_date}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <Clock size={13} /> {booking.time_slot}
          </span>
        </p>
      </div>

      <div className="flex flex-wrap gap-2">
        {booking.status === 'pending' && (
          <>
            <button
              onClick={onConfirm}
              className="inline-flex items-center gap-1.5 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-full text-sm font-medium transition-colors"
              data-testid={`confirm-${booking.id}`}
            >
              <Check size={14} /> Confirm
            </button>
            <button
              onClick={onDecline}
              className="inline-flex items-center gap-1.5 bg-zinc-800 hover:bg-red-900/40 text-red-300 px-4 py-2 rounded-full text-sm font-medium transition-colors border border-red-900/30"
              data-testid={`decline-${booking.id}`}
            >
              <X size={14} /> Decline
            </button>
          </>
        )}
        <button
          onClick={onDelete}
          className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-zinc-800 hover:bg-red-900/40 text-gray-400 hover:text-red-300 transition-colors"
          title="Delete booking"
          data-testid={`delete-${booking.id}`}
        >
          <Trash2 size={14} />
        </button>
      </div>
    </div>

    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 text-sm border-t border-red-900/20 pt-4">
      <Detail icon={User} label="Client">{booking.client_name}</Detail>
      <Detail icon={Mail} label="Email">
        <a href={`mailto:${booking.client_email}`} className="text-red-400 hover:underline">
          {booking.client_email}
        </a>
      </Detail>
      {booking.client_phone && (
        <Detail icon={Phone} label="Phone">
          <a href={`tel:${booking.client_phone}`} className="text-red-400 hover:underline">
            {booking.client_phone}
          </a>
        </Detail>
      )}
      {booking.project_type && <Detail label="Project">{booking.project_type}</Detail>}
      {booking.location && (
        <Detail icon={MapPin} label="Location">
          {booking.location}
        </Detail>
      )}
    </div>

    {booking.message && (
      <div className="mt-4 bg-black/40 border-l-2 border-red-600 px-4 py-3 rounded text-gray-300 text-sm">
        {booking.message}
      </div>
    )}
  </div>
);

const Detail = ({ icon: Icon, label, children }) => (
  <div className="flex items-start gap-2">
    {Icon ? <Icon size={14} className="text-gray-500 mt-0.5 flex-shrink-0" /> : null}
    <div>
      <div className="text-xs uppercase tracking-wider text-gray-500">{label}</div>
      <div className="text-gray-200">{children}</div>
    </div>
  </div>
);

export default AdminBookings;
